// The map page: a region map whose nodes show the best hunt in each area for
// the chosen creature and metric, ringed by risk, a world inset to switch
// regions, and an area panel with one card per hunt. Picking a hunt switches
// right away; the map never moves you on its own. The List view shows the
// region's hunts as rows, sorted by any column and filtered by element and
// Bestiary state.
import { psecTabs, psecWrap } from './psec.ts';
import { CONFIG } from '../shared/config.ts';
import { SPECIES } from '../shared/species.ts';
import { ELEMENTS, ELEMENT_IDS } from '../shared/elements.ts';
import { GROUNDS, GROUND, AREAS, AREA, REGIONS, groundUnlocked, type GroundDef } from '../shared/grounds.ts';
import { ITEM, BOSS_TOKEN } from '../shared/items.ts';
import type { HuntEstimate } from '../shared/analyser.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { spriteUrl } from './assets.ts';
import { esc, num, elChip, cname, estimate, RISK_TEXT, RISK_CLASS } from './fmt.ts';
import { leadCreature, earlyFormLine } from './hud.ts';
import { ICON } from './icons.ts';
import { regionMapUrl, type MapPoint } from './regionmap.ts';
import { trailMapLink } from './trail.ts';

try { const m = localStorage.getItem('mi-map-metric'); if (m === 'xp' || m === 'coins' || m === 'level') S.mapMetric = m; } catch { /* storage off */ }

/** Map or List, remembered on this device. */
let view: 'map' | 'list' = 'map';
try { if (localStorage.getItem('mi-map-view') === 'list') view = 'list'; } catch { /* storage off */ }
/** The List view's sort column and direction, and its filters ('' is all). */
type ListSort = 'name' | 'area' | 'level' | 'caught' | 'xp' | 'coins' | 'risk';
let listSort: ListSort = 'level', listDesc = false, listEl = '', listDex: '' | 'caught' | 'new' | 'unseen' = '';

/** The creature the estimates are for: the lead unless another team member is picked. */
let rateFor: number | null = null;
function rater(): CreatureDTO | undefined {
  const t = S.trainer!;
  if (rateFor !== null && t.team.includes(rateFor)) return S.creatures.get(rateFor);
  return leadCreature();
}

const BOSS_OF: Record<string, string> = Object.fromEntries(Object.entries(BOSS_TOKEN).map(([boss, token]) => [token, boss]));
const metricOf = (e: HuntEstimate) => (S.mapMetric === 'coins' ? e.coinsPerHour : e.xpPerHour);
const metricLabel = (g: GroundDef, e: HuntEstimate | null) => S.mapMetric === 'level' || !e ? `Lv ${g.level}` : S.mapMetric === 'coins' ? `${num(e.coinsPerHour)}/h` : `${num(e.xpPerHour)} XP/h`;

/** An area's best open hunt for the chosen metric (by level for "Level"), with its estimate. */
function bestIn(areaId: string, c: CreatureDTO | undefined) {
  const t = S.trainer!;
  const hunts = GROUNDS.filter((g) => g.area === areaId);
  const open = hunts.filter((g) => groundUnlocked(g, t.level));
  if (!open.length || !c) return { g: hunts[0], e: null as HuntEstimate | null, open: !!open.length };
  let best = open[0], be = estimate(c, best);
  for (const g of open.slice(1)) {
    const e = estimate(c, g);
    const better = S.mapMetric === 'level' ? g.level > best.level : metricOf(e) > metricOf(be);
    if (better) { best = g; be = e; }
  }
  return { g: best, e: be, open: true };
}

/** The best safe open hunt in a region for the metric (XP when the map shows levels), if it beats the current hunt. */
function betterHunt(region: string, c: CreatureDTO | undefined) {
  const t = S.trainer!;
  if (!c) return null;
  const pick = (e: HuntEstimate) => (S.mapMetric === 'coins' ? e.coinsPerHour : e.xpPerHour);
  let best: { g: GroundDef; e: HuntEstimate } | null = null;
  for (const g of GROUNDS) {
    if (g.region !== region || !groundUnlocked(g, t.level)) continue;
    const e = estimate(c, g);
    if (e.risk !== 'safe') continue;
    if (!best || pick(e) > pick(best.e)) best = { g, e };
  }
  const cur = GROUND[t.ground];
  if (!best || best.g.id === t.ground) return null;
  if (cur && cur.region === region && pick(best.e) <= pick(estimate(c, cur)) * 1.05) return null;
  return best;
}

function huntCard(g: GroundDef, c: CreatureDTO | undefined) {
  const t = S.trainer!;
  const sp = SPECIES[g.species];
  const open = groundUnlocked(g, t.level);
  const seen = (t.dex[g.species]?.seen ?? 0) > 0;
  const e = c ? estimate(c, g) : null;
  const here = t.ground === g.id;
  const cur = GROUND[t.ground];
  const away = cur && cur.region !== g.region;
  const match = e ? (e.mult > 1 ? '<span class="gold-text">Super effective</span>' : e.mult < 1 ? 'Resisted' : 'Neutral') : '';
  const boss = g.bossToken ? BOSS_OF[g.bossToken] : '';
  const early = earlyFormLine(g);
  const btn = !open ? '' : here ? '<button class="btn" disabled>Hunting now</button>' : `<button class="btn primary" data-act="map-hunt" data-id="${g.id}">${away ? 'Travel and hunt' : 'Hunt here'}</button>`;
  return `<div class="hcard${here ? ' here' : ''}${open ? '' : ' locked'}" data-key="${g.id}">
    <div class="hc-top"><img src="${spriteUrl(g.species)}" alt=""${seen ? '' : ' class="unseen"'}>
      <div class="grow"><div><b>${seen ? esc(sp.name) : '???'}</b> <span class="muted">Lv ${g.level}</span></div>
        <div class="hc-sub">${sp.elements.map(elChip).join('')} <span class="muted">${sp.rarity}${match ? ` · ${match}` : ''}</span></div></div>${btn}</div>
    ${early ? `<p class="hc-note">${early}</p>` : ''}
    ${open && e ? `<div class="tiles"><div><b>${num(e.xpPerHour)}</b><span>XP per hour</span></div><div><b>${num(e.coinsPerHour)}</b><span>coins per hour</span></div><div><b>${e.secondsPerKill.toFixed(1)} s</b><span>per kill</span></div></div>` : ''}
    ${!open ? `<div class="hc-lock"><span>Opens at Lv ${g.level}</span><span class="muted">Lv ${t.level} / ${g.level}</span></div><div class="bar thin xp"><i style="width:${Math.min(100, (t.level / g.level) * 100)}%"></i></div>` : ''}
    <div class="hc-foot">${open && e ? `<span class="pill ${e.risk === 'safe' ? 'good' : e.risk === 'ok' ? 'amber' : 'bad'}">${RISK_TEXT[e.risk]}</span>` : ''}${boss ? `<span class="muted">Drops <b class="ink">${esc(ITEM[g.bossToken!].name)}</b> · 1 in ${num(CONFIG.bossTokenOneIn)}</span>` : ''}</div>
  </div>`;
}

/** Where each region sits on the world inset: Midgard north-west, Hellas south-east, Yomi north-east. */
const INSET_POS = ['left:12px;top:14px', 'right:14px;bottom:12px', 'right:14px;top:14px'];

/** The last region map drawn, so a resize can repaint it at the new size. */
let artArgs: { region: string; points: MapPoint[] } | null = null;
function mapBoxSize() {
  const el = document.querySelector<HTMLElement>('.mapart');
  if (el?.clientWidth) return { w: el.clientWidth, h: el.clientHeight };
  return { w: Math.max(320, Math.min(1600, innerWidth) - 458), h: Math.max(480, Math.min(760, innerHeight - 210)) };
}
/** Repaints the region map when the box is not the size it was painted for (first open, window resize). */
function syncArt() {
  const img = document.querySelector<HTMLImageElement>('.mapart > img.mart');
  if (!img || !artArgs) return;
  const box = mapBoxSize(), url = regionMapUrl(artArgs.region, artArgs.points, box.w, box.h);
  if (img.getAttribute('src') !== url) img.setAttribute('src', url);
}
let resizeTimer = 0;
addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = window.setTimeout(() => { syncArt(); fitLabels(); }, 200); });
void document.fonts?.ready.then(() => fitLabels());

/**
 * Keep every area's name readable. On a phone the map is narrow for its labels: those at the edge were cut off by
 * the frame, neighbours ran into each other, and a later area's ring covered an earlier one's name. Labels that
 * touch move apart sideways (a few passes) and stay inside the frame; a row too long for the width steps down
 * instead (the lower or right-hand label of a pair); one that still runs into another area's ring is drawn above
 * it. Wider maps have room, so nothing moves there.
 */
function fitLabels() {
  const box = document.querySelector<HTMLElement>('.mapart');
  if (!box) return;
  const nodes = [...box.querySelectorAll<HTMLElement>('.mnode')];
  const lbls = nodes.map((n) => n.querySelector<HTMLElement>('.lbl'));
  for (const l of lbls) if (l) l.style.translate = '';
  for (const n of nodes) n.style.zIndex = '';
  const frame = box.getBoundingClientRect(), PAD = 3;
  const at = lbls.map((l) => l?.getBoundingClientRect() ?? null);
  const dx = at.map(() => 0), dy = at.map(() => 0);
  const inFrame = () => at.forEach((r, i) => {
    if (!r) return;
    if (r.left + dx[i] < frame.left + PAD) dx[i] = frame.left + PAD - r.left;
    else if (r.right + dx[i] > frame.right - PAD) dx[i] = frame.right - PAD - r.right;
  });
  inFrame();
  for (let pass = 0; pass < 4; pass++) {
    let moved = false;
    for (let i = 0; i < at.length; i++) for (let j = i + 1; j < at.length; j++) {
      const a = at[i], b = at[j];
      if (!a || !b || a.bottom <= b.top || b.bottom <= a.top) continue;
      const [l, r] = a.left + dx[i] <= b.left + dx[j] ? [i, j] : [j, i];
      const over = at[l]!.right + dx[l] + PAD - (at[r]!.left + dx[r]);
      if (over > 0.5) { dx[l] -= over / 2; dx[r] += over / 2; moved = true; }
    }
    inFrame();
    if (!moved) break;
  }
  // What still touches: the lower label of the pair (the right-hand one on the same line) steps down clear of it.
  for (let pass = 0; pass < 3; pass++) {
    for (let i = 0; i < at.length; i++) for (let j = i + 1; j < at.length; j++) {
      const a = at[i], b = at[j];
      if (!a || !b) continue;
      if (a.left + dx[i] >= b.right + dx[j] || b.left + dx[j] >= a.right + dx[i]) continue;
      const ta = a.top + dy[i], tb = b.top + dy[j];
      if (ta >= b.bottom + dy[j] || tb >= a.bottom + dy[i]) continue;
      const low = tb > ta || (tb === ta && b.left + dx[j] > a.left + dx[i]) ? j : i, high = low === i ? j : i;
      dy[low] += at[high]!.bottom + dy[high] + 1 - (at[low]!.top + dy[low]);
    }
  }
  at.forEach((r, i) => { if (r && (Math.abs(dx[i]) >= 0.5 || dy[i] >= 0.5)) lbls[i]!.style.translate = `${Math.round(dx[i])}px ${Math.round(dy[i])}px`; });
  // A name that runs into another area's ring is drawn above that area (a few passes, for names over rings over names).
  const rings = nodes.map((n) => n.querySelector<HTMLElement>('.ring')?.getBoundingClientRect());
  const z = nodes.map(() => 1);
  for (let pass = 0; pass < 3; pass++) {
    at.forEach((r, i) => {
      if (!r) return;
      const L = r.left + dx[i], R = r.right + dx[i], T = r.top + dy[i], B = r.bottom + dy[i];
      rings.forEach((g, j) => { if (g && j !== i && L < g.right && R > g.left && T < g.bottom && B > g.top && z[i] <= z[j]) z[i] = z[j] + 1; });
    });
  }
  z.forEach((v, i) => { if (v > 1) nodes[i].style.zIndex = String(v); });
}

const CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
const RISK_RANK = { safe: 0, ok: 1, risky: 2 } as const;

/** The List view: every hunt of the region as a row. */
function listView(region: string, c: CreatureDTO | undefined): string {
  const t = S.trainer!;
  const cur = GROUND[t.ground];
  const rows = GROUNDS.filter((g) => g.region === region).map((g) => {
    const open = groundUnlocked(g, t.level), d = t.dex[g.species];
    const seen = (d?.seen ?? 0) > 0, caught = (d?.caught ?? 0) > 0;
    return { g, open, seen, caught, e: open && c ? estimate(c, g) : null };
  }).filter((r) => (!listEl || SPECIES[r.g.species].elements.includes(listEl as never))
    && (listDex === '' || (listDex === 'caught' ? r.caught : listDex === 'new' ? !r.caught : !r.seen)));
  type Row = (typeof rows)[number];
  // Locked hunts stay below open ones for every column but Level and Area.
  const key = (r: Row): number | string => {
    switch (listSort) {
      case 'name': return r.seen ? SPECIES[r.g.species].name : '\uffff';
      case 'area': return r.g.area;
      case 'caught': return r.caught ? 1 : 0;
      case 'xp': return r.e?.xpPerHour ?? -1;
      case 'coins': return r.e?.coinsPerHour ?? -1;
      case 'risk': return r.e ? RISK_RANK[r.e.risk] : 9;
      default: return r.g.level;
    }
  };
  const byLevel = listSort === 'level' || listSort === 'area';
  rows.sort((a, b) => {
    if (!byLevel && a.open !== b.open) return a.open ? -1 : 1;
    const ka = key(a), kb = key(b);
    const d = typeof ka === 'string' ? ka.localeCompare(kb as string) : ka - (kb as number);
    return (listDesc ? -d : d) || a.g.level - b.g.level || a.g.id.localeCompare(b.g.id);
  });
  const th = (k: ListSort, label: string, cls = '') => `<th class="${cls}"><button class="${listSort === k ? 'on' : ''}" data-act="ml-sort" data-id="${k}" aria-sort="${listSort === k ? (listDesc ? 'descending' : 'ascending') : 'none'}">${label}${listSort === k ? (listDesc ? ' ▾' : ' ▴') : ''}</button></th>`;
  const body = rows.map(({ g, open, seen, caught, e }) => {
    const sp = SPECIES[g.species], here = t.ground === g.id;
    const away = cur && cur.region !== g.region;
    const act = !open ? `<span class="ml-lock">${ICON.lock}Lv ${num(g.level)}</span>`
      : here ? '<span class="pill lead">Here</span>'
      : `<button class="btn small primary" data-act="map-hunt" data-id="${g.id}">${away ? 'Travel' : 'Hunt'}</button>`;
    return `<tr class="${here ? 'on' : ''}${open ? '' : ' locked'}" data-key="ml-${g.id}">
      <td class="ml-sp"><span><img src="${spriteUrl(g.species)}" alt=""${seen ? '' : ' class="unseen"'}><b>${seen ? esc(sp.name) : '???'}</b>${caught ? `<i class="ml-ck" title="Caught">${CHECK}</i>` : ''}</span></td>
      <td class="opt">${esc(AREA[g.area].name)}</td>
      <td class="num">${num(g.level)}</td>
      <td class="opt ml-el">${sp.elements.map(elChip).join('')}</td>
      <td class="ml-c opt">${caught ? `<i class="ml-ck" title="Caught">${CHECK}</i>` : ''}</td>
      <td class="num">${e ? num(e.xpPerHour) : ''}</td>
      <td class="num opt">${e ? num(e.coinsPerHour) : ''}</td>
      <td>${e ? `<i class="ml-risk ${RISK_CLASS[e.risk]}" title="${RISK_TEXT[e.risk]}"></i>` : ''}</td>
      <td class="ml-act">${act}</td></tr>`;
  }).join('');
  const bar = `<div class="ml-bar">
    <div class="seg" role="group" aria-label="Region">${REGIONS.map((r) => `<button class="${r.id === region ? 'on' : ''}" data-region="${r.id}" aria-pressed="${r.id === region}">${t.level >= r.base ? '' : ICON.lock}${r.name}</button>`).join('')}</div>
    <select data-ml-el aria-label="Element"><option value="">All elements</option>${ELEMENT_IDS.map((el) => `<option value="${el}"${listEl === el ? ' selected' : ''}>${ELEMENTS[el].name}</option>`).join('')}</select>
    <select data-ml-dex aria-label="Bestiary"><option value="">All species</option>${([['caught', 'Caught'], ['new', 'Not caught'], ['unseen', 'Not seen']] as const).map(([v, n]) => `<option value="${v}"${listDex === v ? ' selected' : ''}>${n}</option>`).join('')}</select></div>`;
  return `<section class="panel mlist">${bar}
    <div class="gscroll"><table class="tbl2 mtbl"><thead><tr>${th('name', 'Creature')}${th('area', 'Area', 'opt')}${th('level', 'Lv', 'num')}<th class="opt">Elements</th>${th('caught', 'Caught', 'opt')}${th('xp', 'XP/h', 'num')}${th('coins', 'Coins/h', 'num opt')}${th('risk', 'Risk')}<th></th></tr></thead>
    <tbody data-fit="map-list">${body}</tbody></table></div></section>`;
}

export function mapPane(selected: string | null): string {
  const t = S.trainer!;
  const region = S.mapRegion;
  const rg = REGIONS.find((r) => r.id === region)!;
  const c = rater();
  const areas = AREAS.filter((a) => a.region === region);
  const cur = GROUND[t.ground];
  const areaId = selected && AREA[selected]?.region === region ? selected : cur?.region === region ? cur.area : areas[0].id;
  const area = AREA[areaId];
  const team = t.team.map((id) => S.creatures.get(id)).filter((x): x is CreatureDTO => !!x);
  const lead = leadCreature();

  const nodes = areas.map((a) => {
    const open = t.level >= a.level;
    const { g, e } = bestIn(a.id, c);
    const seen = (t.dex[g.species]?.seen ?? 0) > 0;
    const here = cur?.area === a.id;
    const risk = open && e ? RISK_CLASS[e.risk] : 'locked';
    return `<button class="mnode ${risk}${here ? ' here' : ''}${a.id === areaId ? ' on' : ''}" style="left:${(a.mx * 100).toFixed(2)}%;top:${(((a.my * 330 + 10) / 350) * 100).toFixed(2)}%" data-area="${a.id}" aria-label="${esc(a.name)}">
      ${here ? '<span class="pin" aria-hidden="true"></span>' : ''}<span class="ring"><img src="${spriteUrl(g.species)}" alt=""${seen && open ? '' : ' class="unseen"'}>${open ? '' : `<i class="lock" aria-hidden="true">${ICON.lock}</i>`}</span>
      <span class="lbl"><b>${esc(a.name)}</b><span>${open ? metricLabel(g, e) : `Opens Lv ${a.level}`}</span></span></button>`;
  }).join('');

  // The painted region map (client/regionmap.ts), sized to the map box; the trail is part of the painting.
  artArgs = { region, points: areas.map((a) => ({ id: a.id, x: a.mx, y: (a.my * 330 + 10) / 350 })) };
  const box = mapBoxSize();
  const art = `<img class="mart" src="${regionMapUrl(artArgs.region, artArgs.points, box.w, box.h)}" alt="" aria-hidden="true">`;
  requestAnimationFrame(() => { syncArt(); fitLabels(); });

  const inset = `<div class="winset"><div class="row between"><b class="cap">World</b></div>
    <div class="wmini">${REGIONS.map((r, i) => `<button class="wreg${r.id === region ? ' on' : ''}${t.level >= r.base ? '' : ' shut'}" data-region="${r.id}" style="${INSET_POS[i] ?? INSET_POS[0]}" title="${t.level >= r.base ? r.name : `${r.name} · Lv ${r.base}`}">${r.name}</button>`).join('')}</div>
    <div class="wlines">${REGIONS.map((r) => {
      const last = AREAS.filter((a) => a.region === r.id).reduce((m, a) => Math.max(m, a.level), 0);
      const youHere = cur?.region === r.id;
      return `<p class="wline">${t.level >= r.base ? `<i class="dot ${youHere ? 'on' : ''}"></i>${r.name} · Lv ${r.base}-${last}` : `<i class="lk" aria-hidden="true">${ICON.lock}</i>${r.name} · Lv ${r.base}`}</p>`;
    }).join('')}</div></div>`;

  const idx = areas.indexOf(area);
  const prev = areas[idx - 1], next = areas[idx + 1];
  const hunts = GROUNDS.filter((g) => g.area === areaId);
  const better = betterHunt(region, c);
  const metricName = S.mapMetric === 'coins' ? 'coins' : 'XP';
  const panel = `<aside class="apanel">
    <div class="ap-head"><div class="row between"><h2>${esc(area.name)}</h2>${cur?.area === area.id ? '<span class="pill lead">You are here</span>' : ''}</div>
      <p class="muted">${rg.name} · area ${area.index + 1}/${areas.length} · Lv ${area.level}</p></div>
    ${better && better.g.area !== areaId ? `<div class="ap-better"><span>Best ${metricName}: <b>${esc(SPECIES[better.g.species].name)}</b> <span class="muted">(${esc(AREA[better.g.area].name)}) · ${num(S.mapMetric === 'coins' ? better.e.coinsPerHour : better.e.xpPerHour)}/h</span></span><button class="btn small primary" data-act="map-hunt" data-id="${better.g.id}">${cur && cur.region !== region ? 'Travel and hunt' : 'Hunt here'}</button></div>` : ''}
    <div class="ap-cards" data-fit="map-hunts">${hunts.map((g) => huntCard(g, c)).join('')}</div>
    <div class="ap-foot"><p class="muted">Visitors 1 in ${CONFIG.visitorOneIn}</p>
      <div class="row">${prev ? `<button class="btn small" data-area="${prev.id}">‹ ${esc(prev.name)}</button>` : ''}${next ? `<button class="btn small" data-area="${next.id}">${esc(next.name)} ›</button>` : ''}</div></div>
  </aside>`;

  const viewSeg = `<div class="seg mview" role="group" aria-label="View">${([['map', 'Map'], ['list', 'List']] as const).map(([v, n]) => `<button class="${view === v ? 'on' : ''}" data-act="map-view" data-id="${v}" aria-pressed="${view === v}">${n}</button>`).join('')}</div>`;
  if (view === 'list') {
    artArgs = null;
    const head = `<div class="mhead">${viewSeg}<span class="crumb">World › <b>${rg.name}</b></span>${trailMapLink(region)}<span class="spacer"></span>
      <label class="muted">Rate for <select data-map-rate aria-label="Rate for">${team.map((x) => `<option value="${x.id}"${x.id === c?.id ? ' selected' : ''}>${esc(cname(x))}${x.id === lead?.id ? ' (lead)' : ''}</option>`).join('')}</select></label></div>`;
    return `${head}${listView(region, c)}`;
  }

  const head = `<div class="mhead">${viewSeg}<span class="crumb">World › <b>${rg.name}</b></span>${trailMapLink(region)}
    <span class="legend"><i class="lg safe"></i>Safe<i class="lg ok"></i>Some risk<i class="lg risky"></i>Risky<i class="lg locked"></i>Locked</span>
    <span class="spacer"></span>
    <label class="muted">Rate for <select data-map-rate aria-label="Rate for">${team.map((x) => `<option value="${x.id}"${x.id === c?.id ? ' selected' : ''}>${esc(cname(x))}${x.id === lead?.id ? ' (lead)' : ''}</option>`).join('')}</select></label>
    <div class="seg" role="group" aria-label="Map shows">${([['xp', 'XP/h'], ['coins', 'Coins/h'], ['level', 'Level']] as const).map(([v, n]) => `<button class="${S.mapMetric === v ? 'on' : ''}" data-act="map-metric" data-id="${v}" aria-pressed="${S.mapMetric === v}">${n}</button>`).join('')}</div></div>`;

  // A phone shows the map or the picked area's hunts, one at a time (client/psec.ts); tapping an area opens it.
  const keys = ['map', 'area'];
  return `${head}${psecTabs('map', [{ key: 'map', title: 'Map' }, { key: 'area', title: area.name }])}<div class="mapwrap">${psecWrap('map', 'map', keys, `<div class="mapbox"><div class="mapart">${art}${nodes}</div>${inset}</div>`)}${psecWrap('map', 'area', keys, panel)}</div>`;
}

/** Map clicks handled here; returns true when handled. */
export function mapClick(el: HTMLElement): boolean {
  if (el.dataset.act === 'map-view') { view = el.dataset.id === 'list' ? 'list' : 'map'; try { localStorage.setItem('mi-map-view', view); } catch { /* storage off */ } return true; }
  if (el.dataset.act === 'ml-sort') {
    const k = el.dataset.id as ListSort;
    // The same column again flips the order; a new one starts with the most useful end first.
    if (listSort === k) listDesc = !listDesc; else { listSort = k; listDesc = k === 'xp' || k === 'coins' || k === 'caught'; }
    return true;
  }
  if (el.dataset.act === 'map-metric') { S.mapMetric = el.dataset.id as typeof S.mapMetric; try { localStorage.setItem('mi-map-metric', S.mapMetric); } catch { /* storage off */ } return true; }
  return false;
}

export function mapChange(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.mlEl !== undefined) { listEl = el.value; return true; }
  if (el.dataset.mlDex !== undefined) { listDex = el.value === 'caught' || el.value === 'new' || el.value === 'unseen' ? el.value : ''; return true; }
  if (el.dataset.mapRate === undefined) return false;
  rateFor = Number(el.value);
  return true;
}
